import { Component, ChangeDetectionStrategy } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { CfPopoverBase } from 'components/popover-host/popover-interface';
import { PopoverController } from 'providers/popover-controller';
import { Nutrients } from '@cf-shared/data-model/food';
import { INFOODS, checkNutrientCalories } from '@cf-shared/data-model/food';

export interface AddMacrosResult {
  name: string;
  nutrients: Nutrients;
}

@Component({
  selector: 'add-macros-popover',
  templateUrl: 'add-macros-popover.html',
  host: { 'class': 'add-macros-popover' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddMacrosPopover extends CfPopoverBase {
  protected LL = LL;
  protected foodName: string = '';
  protected calories: number | undefined = undefined;
  protected protein: number | undefined = undefined;
  protected fat: number | undefined = undefined;
  protected carbohydrates: number | undefined = undefined;
  protected fiber: number | undefined = undefined;
  protected cholesterol: number | undefined = undefined;

  get showCalorieWarning(): boolean {
    // only show warning if calories are defined and at least one macro is defined
    if (this.calories === undefined || this.calories === null) {
      return false;
    }

    const hasMacros = (this.protein !== undefined && this.protein !== null) ||
                      (this.fat !== undefined && this.fat !== null) ||
                      (this.carbohydrates !== undefined && this.carbohydrates !== null) ||
                      (this.fiber !== undefined && this.fiber !== null);

    if (!hasMacros) {
      return false;
    }

    const nutrients: Nutrients = {};
    if (this.calories !== undefined) nutrients[INFOODS.Calories] = this.calories;
    if (this.protein !== undefined) nutrients[INFOODS.Protein] = this.protein || 0;
    if (this.fat !== undefined) nutrients[INFOODS.Fat] = this.fat || 0;
    if (this.carbohydrates !== undefined) nutrients[INFOODS.CarbNet] = this.carbohydrates || 0;
    if (this.fiber !== undefined) nutrients[INFOODS.Fiber] = this.fiber || 0;

    const { correct } = checkNutrientCalories(nutrients);
    return !correct;
  }

  static async present(
    popoverCtrl: PopoverController,
    targetElement: HTMLElement,
  ): Promise<AddMacrosResult | null> {
    const result = await popoverCtrl.present(
      AddMacrosPopover,
      {},
      {
        animate: true,
        targetFromElement: targetElement,
        showBackdrop: true,
      }
    );

    return result.data;
  }

  constructor(
    private popoverCtrl: PopoverController,
    
  ) {
    super();
  }

  onWillDismiss(data: any) {
    if (data.reason === 'save') {
      const nutrients: Nutrients = {};

      if (this.calories !== undefined && this.calories !== null) {
        nutrients[INFOODS.Calories] = this.calories;
      }
      if (this.protein !== undefined && this.protein !== null) {
        nutrients[INFOODS.Protein] = this.protein;
      }
      if (this.fat !== undefined && this.fat !== null) {
        nutrients[INFOODS.Fat] = this.fat;
      }
      if (this.carbohydrates !== undefined && this.carbohydrates !== null) {
        nutrients[INFOODS.CarbNet] = this.carbohydrates;
      }
      if (this.fiber !== undefined && this.fiber !== null) {
        nutrients[INFOODS.Fiber] = this.fiber;
      }
      if (this.cholesterol !== undefined && this.cholesterol !== null) {
        nutrients[INFOODS.Cholesterol] = this.cholesterol;
      }

      data.data = {
        name: this.foodName || LL.addMacros.defaultFoodName(),
        nutrients
      };
    } else {
      data.data = null;
    }
  }

  onSaveClick() {
    void this.popoverCtrl.dismiss(this, { reason: 'save' });
  }

  onCancelClick() {
    void this.popoverCtrl.dismiss(this, { reason: 'cancel' });
  }

  onCloseClick() {
    void this.popoverCtrl.dismiss(this, { reason: 'cancel' });
  }
}
